import * as React from 'react'
import styled, { css } from 'styled-components/macro'

import {
  ButtonProps,
  ButtonColorTheme,
  ButtonSize,
  ButtonSizeLegacy,
  ButtonVariant,
  StyledButtonProps,
  StyledTextButtonProps,
  ThemeColor,
} from './types'
import { Spinner } from '../Spinner'
import { track } from '../../utils/track'

export const colorTheme: { [key: string]: ButtonColorTheme } = {
  // Outline danger — DS alert (--status-danger) on a transparent fill.
  danger: {
    background: {
      active: 'var(--status-danger)',
      default: 'transparent',
      hover: 'transparent',
    },
    border: {
      active: 'var(--status-danger)',
      default: 'var(--status-danger)',
      hover: 'var(--status-danger)',
      focus: 'var(--status-danger)',
    },
    color: 'var(--status-danger)',
  },

  // Primary — DS solid deep-purple (--action-primary) with on-primary label.
  primary: {
    background: {
      active: 'var(--action-primary-hover)',
      default: 'var(--action-primary)',
      hover: 'var(--action-primary-hover)',
    },
    color: 'var(--action-on-primary)',
  },
  // Commerce — DS moss accent (--action-accent) for value/commerce actions
  // (add to cart, reload inventory, collect payment). §4.4 + §4.1 footer rule.
  commerce: {
    background: {
      active: 'var(--action-accent-hover)',
      default: 'var(--action-accent)',
      hover: 'var(--action-accent-hover)',
    },
    color: 'var(--action-on-accent)',
  },
  // Secondary — DS outline (§4.4): card fill, brand border, deep-purple ink.
  // Pressed fill shares hover's THEME-BOUND brand wash (primary/commerce set
  // the active==hover precedent; the press still reads via the shadow drop +
  // label nudge in buttonStyles). A theme-independent light primitive here
  // goes light-on-light under the dark themes' light --action-primary ink.
  secondary: {
    background: {
      active: 'var(--surface-brand-soft)',
      default: 'var(--surface-card)',
      hover: 'var(--surface-brand-soft)',
    },
    border: {
      active: 'var(--border-brand)',
      default: 'var(--border-brand)',
      hover: 'var(--border-brand)',
      focus: 'var(--focus-ring)',
    },
    color: 'var(--action-primary)',
  },
  // Tertiary — DS quiet outline: deep-purple ink on a brand hairline.
  // Pressed fill: same theme-bound wash + rationale as secondary above.
  tertiary: {
    background: {
      active: 'var(--surface-brand-soft)',
      default: 'transparent',
      hover: 'var(--surface-brand-soft)',
    },
    border: {
      active: 'var(--border-brand)',
      default: 'var(--border-brand)',
      hover: 'var(--border-brand)',
      focus: 'var(--focus-ring)',
    },
    color: 'var(--action-primary)',
  },
  // XTREME — DS solid danger emphasis.
  XTREME: {
    background: {
      active: 'var(--status-danger)',
      default: 'var(--status-danger)',
      hover: 'var(--status-danger)',
    },
    border: {
      active: 'var(--status-danger)',
      default: 'var(--status-danger)',
      hover: 'var(--status-danger)',
      focus: 'var(--status-danger)',
    },
    color: 'var(--status-danger-ink)',
  },
}

/**
 * DS §4.4 variant name → internal palette key. Only `outline` differs from its
 * palette key (→ secondary); the rest are identity mappings.
 */
const variantToColor: Record<ButtonVariant, ThemeColor> = {
  primary: 'primary',
  commerce: 'commerce',
  outline: 'secondary',
  tertiary: 'tertiary',
  danger: 'danger',
  // `text` is served by the frameless branch below and never consults a palette
  // key; this entry only satisfies the exhaustive Record type.
  text: 'tertiary',
}

/** DS size aliases → legacy size tokens (default is the largest). */
const sizeAliasToLegacy: Record<string, ButtonSizeLegacy> = {
  sm: 'small',
  md: 'medium',
  lg: 'default',
}

const normalizeSize = (size: ButtonSize): ButtonSizeLegacy =>
  (sizeAliasToLegacy[size] as ButtonSizeLegacy) ?? (size as ButtonSizeLegacy)

/** DS §4.4 size rungs — sm / md / lg ≈ 38 / 44 / 54. */
const sizeMinHeight: Record<ButtonSizeLegacy, string> = {
  small: '38px',
  medium: '44px',
  default: '54px',
}

const sizeInlinePadding: Record<ButtonSizeLegacy, string> = {
  small: '0 0.75rem',
  medium: '0 1rem',
  default: '0 1.25rem',
}

export const buttonStyles = css<StyledButtonProps>`
  background: ${({ colorPalette }) => colorPalette.background.default};
  color: ${({ colorPalette }) => colorPalette.color};
  align-items: center;
  border-radius: var(--radius-pill);
  box-sizing: border-box;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font-family: inherit;
  font-weight: var(--font-weight-medium);
  font-size: var(--mantine-font-size-sm);
  justify-content: center;
  line-height: var(--mantine-line-height-snug);
  margin: 0;
  min-height: ${({ size }) => sizeMinHeight[size]};
  position: relative;
  /* Outline / tertiary use 1.5px --border-brand (DS §4.4). Transparent 1.5px
     on solid fills keeps geometry aligned across variants. */
  border: ${({ colorPalette }) =>
    colorPalette.border
      ? `1.5px solid ${colorPalette.border.default}`
      : '1.5px solid transparent'};
  padding: ${({ size }) => sizeInlinePadding[size]};
  text-align: center;
  /* Shared truth: sentence case. Page CSS must not force uppercase without a
     documented exception (ENG-2600 / Cashier CartStyles = PR2). */
  text-transform: none;
  transition: all var(--dur-base) var(--ease-out);
  user-select: none;
  width: ${({ fullWidth }) => (!!fullWidth ? '100%' : 'auto')};

  ${({ $loading }) =>
    $loading &&
    css`
      outline: none;
      pointer-events: none;

      /* Keep the label occupying layout so the button keeps its width while
         the absolutely-centered spinner overlays it. */
      > div {
        visibility: hidden;
      }
    `}

  div {
    display: flex;
    align-items: center;
    justify-content: center;
    fill: currentcolor;
    ${({ hasAffixIcon }) => hasAffixIcon && 'gap: 0.5rem;'}

    > svg {
      height: 1rem;
      width: 1rem;
      transform: scale3d(1.5, 1.5, 1.5);
    }
  }

  :disabled {
    background: var(--surface-queue-footer);
    border: 1.5px solid var(--surface-queue-footer);
    color: var(--text-disabled);
    cursor: not-allowed;
    pointer-events: none;
  }

  :enabled:active {
    background: ${({ colorPalette }) => colorPalette.background.active};
    box-shadow: none;
    outline: none;
    ${({ colorPalette }) =>
      colorPalette.border && `border-color: ${colorPalette.border.active};`}
  }

  :enabled:active > div {
    transform: translateY(0.5px);
  }

  :enabled:hover:not(:active),
  :enabled:focus-visible:not(:active) {
    background: ${({ colorPalette }) => colorPalette.background.hover};
    box-shadow: var(--shadow-md);
    ${({ colorPalette }) =>
      colorPalette.border && `border-color: ${colorPalette.border.hover};`}
  }

  :enabled:focus-visible:not(:active) {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
    ${({ colorPalette }) =>
      colorPalette.border && `border-color: ${colorPalette.border.focus};`}
  }
`

const StyledButton = styled.button<StyledButtonProps>`
  ${buttonStyles}
`

/**
 * `variant="text"` (frameless) render. This CSS is moved verbatim from the
 * former components/ButtonFrameless StyledButton so the ~36 ButtonFrameless
 * call sites (now routed through the adapter → this element) stay pearl-
 * THEME-identical. Sole deliberate departure from the moved CSS: the resting
 * ink and the non-whiteBg hover/press plates were theme-independent Layer-1
 * primitives (--deep-purple ink, --pearl plates) — dark-on-dark ink and a
 * light-on-light press flash under dusk/midnight, the same bug fixed for the
 * secondary/tertiary palettes above. They now ride --action-primary /
 * --surface-brand-soft, which resolve to exactly those primitives in the
 * pearl theme and re-bind per theme. Do not "tidy" the rest onto
 * buttonStyles: the frameless geometry (no border, --radius-sm, transparent
 * ground, size→height, stacked column, whiteBg hover) is deliberately
 * distinct from the framed variants above.
 */
const StyledTextButton = styled.button<StyledTextButtonProps>`
  flex-shrink: 0;
  font-family: inherit;
  font-weight: var(--font-weight-medium);
  font-size: var(--mantine-font-size-sm);
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--action-primary);
  cursor: pointer;
  fill: currentcolor;
  height: ${({ size, stacked }) =>
    stacked ? '100%' : size === 'small' ? '2rem' : '3rem'};
  line-height: var(--mantine-line-height-flat);
  min-width: 3rem;
  outline: none;
  padding: ${({ size }) => (size === 'small' ? '0.5rem 1rem' : '1rem')};
  transition: box-shadow 100ms linear;
  user-select: none;

  svg {
    height: 1rem;
    width: 1rem;
    transform: scale3d(1.5, 1.5, 1.5);
    user-select: inherit;

    ${({ icon, iconOnly, iconRight, stacked }) =>
      !!icon &&
      !iconOnly &&
      (stacked
        ? 'margin-bottom: 0.75rem'
        : iconRight
        ? 'margin-left: 0.75rem'
        : 'margin-right: 0.75rem')};

    ${({ iconOnly }) =>
      iconOnly &&
      css`
        margin: 0;
      `}
  }

  div {
    align-items: center;
    display: flex;
    flex-direction: ${({ iconRight, stacked }) =>
      stacked ? 'column' : iconRight ? 'row-reverse' : 'row'};
    justify-content: center;
  }

  :disabled {
    color: var(--text-disabled);
    font-weight: var(--font-weight-regular);
    pointer-events: none;
  }

  :hover {
    background: ${({ whiteBg }) =>
      !!whiteBg ? 'var(--surface-sunken)' : 'var(--surface-brand-soft)'};
  }
  :active {
    background: var(--surface-brand-soft);
  }
  :focus-visible:not(:active):not(:hover) {
    background: transparent;
    box-shadow: inset 0 0 0 2px var(--focus-ring);
  }
`

/**
 * Primary UI component for user interaction
 *
 *  Buttons are clickable elements that are used to trigger actions.
 *  They communicate calls to action to the user and allow users to interact with pages in a variety of ways.
 *
 * - Dialogs
 * - Modals
 * - Forms
 */
export const Button = ({
  color = 'tertiary',
  variant,
  children,
  disabled = false,
  fullWidth = false,
  loading = false,
  iconLeft,
  iconRight,
  icon,
  stacked = false,
  whiteBg = false,
  size = 'default',
  type = 'button',
  tracking,
  onClick,
  ...rest
}: ButtonProps) => {
  // `variant="text"` is the absorbed frameless button. It renders a distinct
  // element (StyledTextButton, CSS moved verbatim from ButtonFrameless) driven
  // by the frameless-only props (icon / iconRight-as-boolean / stacked /
  // whiteBg) rather than the color palette — kept structurally identical to
  // the former ButtonFrameless so its call sites stay pearl-theme-identical
  // (state paints are theme-bound; see StyledTextButton).
  if (variant === 'text') {
    const iconRightFlag = !!iconRight
    return (
      <StyledTextButton
        disabled={disabled}
        icon={!!icon}
        iconOnly={!!icon && !children}
        size={size}
        stacked={stacked}
        type={type}
        iconRight={iconRightFlag}
        whiteBg={whiteBg}
        onClick={(e) => {
          onClick && onClick(e)
          tracking && track(tracking)
        }}
        {...rest}
      >
        <div>
          {icon}
          {children}
        </div>
      </StyledTextButton>
    )
  }

  // `variant` is the preferred DS API, but historically some call sites passed
  // it as a dead decorative prop (e.g. variant="light"/"text") while relying on
  // `color` for the palette. Fall back to `color` for any value that is not a
  // recognized DS variant so those sites render byte-identically.
  const paletteKey: ThemeColor = (variant && variantToColor[variant]) || color
  const hasAffixIcon = iconLeft != null || iconRight != null
  return (
    <StyledButton
      colorPalette={colorTheme[paletteKey]}
      disabled={disabled}
      fullWidth={fullWidth}
      hasAffixIcon={hasAffixIcon}
      $loading={loading}
      size={normalizeSize(size)}
      type={type}
      onClick={(e) => {
        onClick && onClick(e)
        tracking && track(tracking)
      }}
      {...rest}
    >
      {loading && (
        <Spinner
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            marginRight: 0,
          }}
        />
      )}
      <div>
        {iconLeft}
        {children}
        {iconRight}
      </div>
    </StyledButton>
  )
}
